import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Typography, Box, Grid, Chip, Divider,
  Table, TableBody, TableCell, TableRow, Button, CircularProgress
} from '@mui/material';
import { ArrowBack, Edit, PictureAsPdf, Email } from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AngebotViewPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [angebot, setAngebot] = useState(location.state?.angebot || null);
  const [loading, setLoading] = useState(!angebot);
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (!angebot && id) {
      fetchAngebot();
    }
  }, [id]);

  const fetchAngebot = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/angebot/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAngebot(response.data);
    } catch (err) {
      console.error('Error fetching angebot:', err);
      alert('Fehler beim Laden des Angebots');
      navigate('/angebot-list');
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!window.confirm('PDF per Email nochmal versenden?')) {
      return;
    }

    setSendingEmail(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/angebot/${angebot._id}/resend-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        alert('Email erfolgreich versendet!');
      } else {
        alert('Fehler beim Versenden: ' + (response.data.message || 'Unbekannter Fehler'));
      }
    } catch (err) {
      console.error('Error resending email:', err);
      alert('Fehler beim Versenden der Email: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingEmail(false);
    }
  };

  if (loading || !angebot) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <AppHeader title="Angebot Details" showBack={true} backTo="/angebot-list" />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Typography>Laden...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader title="Angebot Details" showBack={true} backTo="/angebot-list" />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header with status and actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5">{angebot.kunde_name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Erstellt am {angebot.datum}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Chip 
              label={angebot.status === 'submitted' ? 'Versendet' : 'Entwurf'} 
              color={angebot.status === 'submitted' ? 'success' : 'warning'}
            />
            {angebot.status === 'submitted' && (
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PictureAsPdf />}
                  size="medium"
                  onClick={async () => {
                    try {
                      // Wenn pdf_id vorhanden, direkt öffnen
                      if (angebot.pdf_id) {
                        window.open(`${API_URL}/angebot/pdf/${angebot.pdf_id}`, '_blank');
                      } else {
                        // Ansonsten PDF generieren
                        const token = localStorage.getItem('token');
                        const response = await axios.get(`${API_URL}/angebot/generate-pdf/${angebot._id}`, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        if (response.data.pdf_id) {
                          window.open(`${API_URL}/angebot/pdf/${response.data.pdf_id}`, '_blank');
                        }
                      }
                    } catch (err) {
                      console.error('Error opening PDF:', err);
                      alert('Fehler beim Öffnen des PDFs');
                    }
                  }}
                >
                  PDF ÖFFNEN
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={sendingEmail ? <CircularProgress size={20} /> : <Email />}
                  size="medium"
                  onClick={handleResendEmail}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? 'Sende...' : 'PDF NOCHMAL SENDEN'}
                </Button>
              </>
            )}
          </Box>
        </Box>

        {/* Customer Info */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Kundendaten</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography><strong>Name:</strong> {angebot.kunde_name}</Typography>
              <Typography><strong>Kundennummer:</strong> {angebot.ku_nr}</Typography>
              <Typography><strong>Adresse:</strong> {angebot.kunde_strasse}, {angebot.kunde_plz} {angebot.kunde_ort}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Email:</strong> {angebot.kunde_email || 'Nicht angegeben'}</Typography>
              <Typography><strong>Telefon:</strong> {angebot.kunde_telefon || 'Nicht angegeben'}</Typography>
              <Typography><strong>Monteur:</strong> {angebot.monteur}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Service Details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Service Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography><strong>Serviceart:</strong> {angebot.serviceart}</Typography>
              <Typography><strong>Montage geplant:</strong> {angebot.montage_geplant}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography><strong>Kundenwunsch:</strong> {angebot.hersteller_wunsch || 'Keine Angabe'}</Typography>
              <Typography><strong>Empfehlung:</strong> {angebot.hersteller_empfehlung || 'Keine Angabe'}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Rooms */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Räume ({angebot.raeume?.length || 0})</Typography>
          {angebot.raeume?.map((raum, idx) => (
            <Box key={idx} sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <strong>Raum {idx + 1}: {raum.raumname || 'Ohne Namen'}</strong>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Dimensionen: {raum.laenge}m (L) × {raum.breite}m (B) × {raum.hoehe}m (H)
                  </Typography>
                  <Typography variant="body2">
                    Fläche: {(raum.breite * raum.laenge).toFixed(2)} m²
                  </Typography>
                  <Typography variant="body2">
                    Volumen: {(raum.breite * raum.laenge * raum.hoehe).toFixed(2)} m³
                  </Typography>
                  <Typography variant="body2">
                    Gerätetyp: {raum.geraetetyp}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Leitungslänge: {raum.leitungslaenge_innen_aussen}m
                  </Typography>
                  <Typography variant="body2">
                    Kondenswasserpumpe: {raum.kondenswasserpumpe ? 'Ja' : 'Nein'}
                  </Typography>
                  <Typography variant="body2">
                    WiFi: {raum.wifi ? 'Ja' : 'Nein'}
                  </Typography>
                  {raum.wunschfarbe && (
                    <Typography variant="body2">
                      Wunschfarbe: {raum.wunschfarbe}
                    </Typography>
                  )}
                </Grid>
                {raum.besonderheiten && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Besonderheiten:</strong> {raum.besonderheiten}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </Paper>

        {/* Outdoor Units */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Außengeräte ({angebot.aussengeraete?.length || 0})</Typography>
          {angebot.aussengeraete?.map((ag, idx) => (
            <Box key={idx} sx={{ mb: 3, p: 2, bgcolor: '#f0f8ff', borderRadius: 1 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                <strong>Außengerät {idx + 1}</strong>
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">Typ: {ag.typ}</Typography>
                  <Typography variant="body2">Montageort: {ag.montageort}</Typography>
                  {ag.montage_auf && (
                    <Typography variant="body2">Montage auf: {ag.montage_auf}</Typography>
                  )}
                  {ag.hoehe && (
                    <Typography variant="body2">Höhe: {ag.hoehe}m</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2">
                    Zugeordnete Räume: {(() => {
                      if (!ag.zugeordnete_raeume || ag.zugeordnete_raeume.length === 0) {
                        return 'Keine';
                      }
                      const raumNamen = [];
                      ag.zugeordnete_raeume.forEach(raumIdx => {
                        if (raumIdx >= 0 && raumIdx < angebot.raeume?.length) {
                          raumNamen.push(angebot.raeume[raumIdx].raumname);
                        }
                      });
                      return raumNamen.join(', ') || 'Keine';
                    })()}
                  </Typography>
                  <Typography variant="body2">
                    Gesamtleitungslänge: {ag.gesamt_leitungslaenge || 0}m
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1 }}>
                    Gesamtkälteleistung: ca. {(() => {
                      let gesamt = 0;
                      ag.zugeordnete_raeume?.forEach(raumIdx => {
                        if (raumIdx >= 0 && raumIdx < angebot.raeume?.length) {
                          gesamt += angebot.raeume[raumIdx].kuehlleistung_kw || 0;
                        }
                      });
                      return gesamt.toFixed(2);
                    })()} kW
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}><strong>Zusatzoptionen:</strong></Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {ag.dachtritt_vorhanden && <Chip label="Dachtritt vorhanden" size="small" />}
                    {ag.kondenswasserwanne && <Chip label="Kondenswasserwanne" size="small" />}
                    {ag.oelauffangwanne && <Chip label="Ölauffangwanne" size="small" />}
                    {ag.einhausung && <Chip label="Einhausung" size="small" />}
                    {ag.ablaufheizung && <Chip label="Ablaufheizung" size="small" />}
                    {ag.vollwaermeschutz && <Chip label={`Vollwärmeschutz${ag.vollwaermeschutz_staerke_mm ? ` (${ag.vollwaermeschutz_staerke_mm}mm)` : ''}`} size="small" />}
                    {ag.montagelift && <Chip label="Montagelift" size="small" color="warning" />}
                    {ag.arbeitsbuehne && <Chip label="Arbeitsbühne" size="small" color="warning" />}
                    {ag.dachlift && <Chip label="Dachlift" size="small" color="warning" />}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ))}
        </Paper>

        {/* Drilling Info */}
        {(angebot.kernbohrungen || angebot.bohrungen || angebot.mauerschlitz) && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Bohrungen & Mauerschlitz</Typography>
            {angebot.kernbohrungen && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Kernbohrungen:</strong> {angebot.kernbohrungen}
              </Typography>
            )}
            {angebot.bohrungen && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Bohrungen:</strong> {angebot.bohrungen}
              </Typography>
            )}
            {angebot.mauerschlitz && (
              <Typography variant="body2">
                <strong>Mauerschlitz:</strong> {angebot.mauerschlitz} m
              </Typography>
            )}
          </Paper>
        )}

        {/* Notes */}
        {angebot.notizen && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Notizen</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {angebot.notizen}
            </Typography>
          </Paper>
        )}

        {/* Images */}
        {angebot.bilder && angebot.bilder.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Fotos ({angebot.bilder.length})</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {angebot.bilder.map((imgObj, idx) => {
                // Handle both old format (URL string) and new format (ImageAttachment object)
                const imgSrc = typeof imgObj === 'string' 
                  ? `${API_URL}${imgObj}` 
                  : imgObj.data;
                const imgDescription = typeof imgObj === 'object' ? imgObj.description : '';
                
                return (
                  <Box key={idx} sx={{ width: 200 }}>
                    <img 
                      src={imgSrc} 
                      alt={`Bild ${idx + 1}`} 
                      style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: '4px' }}
                    />
                    {imgDescription && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {imgDescription}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBack />}
            onClick={() => navigate('/angebot-list')}
          >
            Zurück zur Liste
          </Button>
          
          {/* Zeige "SPEICHERN & SENDEN" Button bei Entwürfen */}
          {angebot.status === 'draft' && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={async () => {
                if (window.confirm('Angebot jetzt versenden?')) {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.put(`${API_URL}/angebot/${angebot._id}`, 
                      { ...angebot, status: 'submitted' },
                      { headers: { Authorization: `Bearer ${token}` }}
                    );
                    // Kein Alert mehr, direkt zur Liste
                    navigate('/angebot-list');
                  } catch (err) {
                    console.error('Error submitting:', err);
                    alert('Fehler beim Versenden');
                  }
                }
              }}
            >
              SPEICHERN & SENDEN
            </Button>
          )}
        </Box>
      </Container>
    </Box>
  );
}
